import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Bell, Plus, Copy, Eye, Calendar, Megaphone, FileText, Store,
  ChevronRight, Loader2, CheckCircle, Clock, User, Building2, AlertCircle
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAnnouncements } from "@/hooks/useAnnouncements";
import { useCondominium } from "@/hooks/useCondominium";
import { useMyBookings } from "@/hooks/useBookings";
import { useFinancialCharges } from "@/hooks/useFinancialCharges";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, formatDistanceToNow, isToday, isYesterday } from "date-fns";
import { ptBR } from "date-fns/locale/pt-BR";
import { toast } from "sonner";

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(cents / 100);

const DashboardMorador = () => {
  let navigate: ReturnType<typeof useNavigate> | null = null;
  try { navigate = useNavigate(); } catch { /* fallback */ }

  const { profile, loading: authLoading } = useAuth();
  const { data: announcements, isLoading: announcementsLoading } = useAnnouncements();
  const { data: condominium } = useCondominium();
  const { data: bookings, isLoading: bookingsLoading } = useMyBookings();
  const { data: charges, isLoading: chargesLoading } = useFinancialCharges();

  // Buscar síndico do condomínio
  const { data: sindico } = useQuery({
    queryKey: ["sindico-profile", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, email")
        .eq("condominium_id", profile.condominium_id)
        .eq("role", "sindico")
        .eq("is_active", true)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.condominium_id,
  });

  const firstName = profile?.full_name?.split(" ")[0] || "Morador";

  // Cobranças do morador (pendentes/vencidas)
  const myCharges = charges?.filter(
    (c) => c.resident_id === profile?.id && (c.status === "pending" || c.status === "overdue")
  ) || [];
  const nextCharge = myCharges.sort((a, b) => a.due_date.localeCompare(b.due_date))[0];

  // Status de pagamento do mês atual
  const allMyCharges = charges?.filter((c) => c.resident_id === profile?.id) || [];
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const thisMonthCharges = allMyCharges.filter((c) => {
    const d = new Date(c.due_date);
    return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
  });
  const thisMonthPaid = thisMonthCharges.filter((c) => c.status === "paid");
  const paymentProgress = thisMonthCharges.length > 0
    ? Math.round((thisMonthPaid.length / thisMonthCharges.length) * 100)
    : 100;
  const isUpToDate = paymentProgress === 100;

  // Próxima reserva
  const today = new Date().toISOString().split("T")[0];
  const nextBooking = bookings?.find(
    (b) => b.booking_date >= today && (b.status === "pending" || b.status === "confirmed")
  );

  // Unidade
  const getUnitLabel = () => {
    if (!profile?.apartment_number) return "Unidade não definida";
    const num = profile.apartment_number;
    const bld = profile.building;
    const isApt = bld || parseInt(num) > 100;
    return bld ? `${isApt ? "Apto" : "Casa"} ${bld}-${num}` : `${isApt ? "Apto" : "Casa"} ${num}`;
  };

  // Data relativa para avisos
  const relativeDate = (dateStr: string) => {
    const d = new Date(dateStr);
    if (isToday(d)) return "Hoje";
    if (isYesterday(d)) return "Ontem";
    return formatDistanceToNow(d, { addSuffix: true, locale: ptBR });
  };

  const currentMonthName = format(new Date(), "MMMM", { locale: ptBR });

  if (authLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center p-8">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Preparando seu dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground">
            Olá, {firstName}
          </h1>
          <p className="text-sm text-muted-foreground">
            {condominium?.name || "Condomínio"} · {getUnitLabel()}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => navigate?.("/mensagens")}
          >
            <Bell className="h-5 w-5" />
          </Button>
          <Button
            className="bg-sky-500 hover:bg-sky-600 text-white"
            onClick={() => navigate?.("/ocorrencias")}
          >
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Novo Chamado</span>
          </Button>
        </div>
      </div>

      {/* 3 Cards principais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Fatura pendente */}
        <Card className="rounded-xl">
          <CardContent className="p-4 space-y-3">
            {chargesLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : nextCharge ? (
              <>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={
                      nextCharge.status === "overdue"
                        ? "bg-red-100 text-red-700 border-red-200"
                        : "bg-yellow-100 text-yellow-700 border-yellow-200"
                    }
                  >
                    {nextCharge.status === "overdue" ? "VENCIDA" : "A VENCER"}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    Vencimento: {format(new Date(nextCharge.due_date), "dd/MM/yyyy")}
                  </span>
                </div>
                <div>
                  <p className="text-3xl font-bold text-foreground">
                    {formatCurrency(nextCharge.amount_cents)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">{nextCharge.description}</p>
                </div>
                <div className="flex flex-col gap-2">
                  {nextCharge.external_payment_link && (
                    <Button
                      className="w-full bg-sky-500 hover:bg-sky-600 text-white"
                      onClick={() => {
                        navigator.clipboard.writeText(nextCharge.external_payment_link || "");
                        toast.success("Link copiado!");
                      }}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copiar Código de Barras
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate?.("/financeiro")}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Ver Detalhes
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-3 text-center">
                <CheckCircle className="h-8 w-8 text-green-500 mb-2" />
                <p className="font-semibold text-foreground">Nenhuma fatura pendente</p>
                <p className="text-sm text-muted-foreground">Você está em dia!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status de pagamento */}
        <Card className="rounded-xl">
          <CardContent className="p-4 flex flex-col items-center justify-center text-center space-y-2">
            <div
              className={`p-3 rounded-full ${isUpToDate ? "bg-green-100" : "bg-yellow-100"}`}
            >
              {isUpToDate ? (
                <CheckCircle className="h-8 w-8 text-green-600" />
              ) : (
                <AlertCircle className="h-8 w-8 text-yellow-600" />
              )}
            </div>
            <p className="text-sm text-muted-foreground capitalize">{currentMonthName}</p>
            <Progress value={paymentProgress} className="h-2 w-full" />
            <Badge
              variant="outline"
              className={
                isUpToDate
                  ? "bg-green-100 text-green-700 border-green-200"
                  : "bg-yellow-100 text-yellow-700 border-yellow-200"
              }
            >
              {isUpToDate ? "PAGO EM DIA" : `${paymentProgress}% PAGO`}
            </Badge>
          </CardContent>
        </Card>

        {/* Próxima reserva */}
        <Card className="rounded-xl">
          <CardContent className="p-4 space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Próxima Reserva</p>
            {bookingsLoading ? (
              <div className="flex items-center justify-center py-3">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : nextBooking ? (
              <>
                <div className="space-y-1">
                  <p className="text-lg font-bold text-foreground capitalize">
                    {format(new Date(nextBooking.booking_date + "T12:00:00"), "EEEE, dd 'de' MMMM", {
                      locale: ptBR,
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {(nextBooking as any).area_name || "Área"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <Clock className="h-3.5 w-3.5 inline mr-1" />
                    {nextBooking.start_time?.slice(0, 5)} - {nextBooking.end_time?.slice(0, 5)}
                  </p>
                </div>
                <Button
                  variant="link"
                  className="p-0 h-auto text-sky-600"
                  onClick={() => navigate?.("/areas-comuns")}
                >
                  Ver meus agendamentos <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-3 text-center">
                <Calendar className="h-6 w-6 text-muted-foreground mb-1" />
                <p className="text-sm text-muted-foreground">Nenhuma reserva agendada</p>
                <Button
                  variant="link"
                  className="p-0 h-auto text-sky-600 mt-1"
                  onClick={() => navigate?.("/areas-comuns")}
                >
                  Reservar agora <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Seu Condomínio + Acesso Rápido */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Acesso Rápido - 2 colunas */}
        <div className="lg:col-span-2">
          <h2 className="text-lg font-semibold text-foreground mb-3">Acesso Rápido</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              {
                icon: Calendar,
                label: "Reservar Espaço",
                sub: "Salão de festas, churrasqueira",
                color: "bg-blue-100 text-blue-600",
                route: "/areas-comuns",
              },
              {
                icon: Megaphone,
                label: "Comunicar Ocorrência",
                sub: "Manutenção, barulho, dúvidas",
                color: "bg-orange-100 text-orange-600",
                route: "/ocorrencias",
              },
              {
                icon: FileText,
                label: "Documentos",
                sub: "Atas, regimento, balancetes",
                color: "bg-purple-100 text-purple-600",
                route: "/avisos",
              },
              {
                icon: Store,
                label: "Classificados",
                sub: "Compra e venda entre vizinhos",
                color: "bg-green-100 text-green-600",
                route: "/mensagens",
              },
            ].map((item) => (
              <Card
                key={item.label}
                className="rounded-xl cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate?.(item.route)}
              >
                <CardContent className="p-3 flex flex-col items-center text-center gap-1.5">
                  <div className={`p-2.5 rounded-xl ${item.color}`}>
                    <item.icon className="h-5 w-5" />
                  </div>
                  <p className="font-medium text-sm text-foreground">{item.label}</p>
                  <p className="text-xs text-muted-foreground leading-tight">{item.sub}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Seu Condomínio */}
        <Card className="rounded-xl bg-gray-900 text-white border-0">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gray-400" />
              <p className="text-sm text-gray-400">Seu Condomínio</p>
            </div>
            <p className="text-lg font-bold">{condominium?.name || "Carregando..."}</p>
            {sindico && (
              <div className="space-y-2 pt-2 border-t border-gray-700">
                <Badge variant="outline" className="bg-gray-800 text-gray-300 border-gray-600 text-xs">
                  SÍNDICO
                </Badge>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-gray-700">
                    <User className="h-4 w-4 text-gray-300" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{sindico.full_name}</p>
                    <p className="text-xs text-gray-400">{sindico.email}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Mural de Avisos */}
      <Card className="rounded-xl">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg">Mural de Avisos</CardTitle>
          <Button
            variant="link"
            className="p-0 h-auto text-sky-600"
            onClick={() => navigate?.("/avisos")}
          >
            Ver todos <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {announcementsLoading ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : announcements && announcements.length > 0 ? (
            announcements.slice(0, 4).map((announcement) => {
              const iconColors = announcement.is_important
                ? "bg-red-100 text-red-600"
                : "bg-blue-100 text-blue-600";
              return (
                <div
                  key={announcement.id}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className={`p-2 rounded-full shrink-0 ${iconColors}`}>
                    {announcement.is_important ? (
                      <AlertCircle className="h-4 w-4" />
                    ) : (
                      <Bell className="h-4 w-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm text-foreground">{announcement.title}</p>
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0"
                      >
                        {isToday(new Date(announcement.created_at || ""))
                          ? "HOJE"
                          : format(new Date(announcement.created_at || ""), "dd/MM")}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {announcement.content}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {relativeDate(announcement.created_at || "")}
                    </p>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-center text-muted-foreground py-3">Nenhum aviso no momento</p>
          )}
        </CardContent>
      </Card>

      
    </div>
  );
};

export default DashboardMorador;
